import React, { useCallback, useEffect, useState } from 'react';
import { courseSupportingDataService, CourseSupportingDataItem } from '../../../../services/courseSupportingDataService';
import { IMAGE_MODEL_OPTIONS } from '../../../../constants/imageModels';

interface ImageConfig {
  lesson_image_model: string;
  artwork_image_model: string;
}

const DEFAULTS: ImageConfig = {
  lesson_image_model: 'gpt-image-1',
  artwork_image_model: 'gpt-image-1',
};

const FIELDS = [
  { key: 'lesson_image_model', label: 'Lesson Images', hint: 'Inline images generated for each lesson (16:9)' },
  { key: 'artwork_image_model', label: 'Course Artwork', hint: 'Course cover image, cropped into three preview variants (1:1 source)' },
] as const;

function parseConfig(raw: string): ImageConfig {
  try {
    const data = JSON.parse(raw);
    return {
      lesson_image_model: data.lesson_image_model || DEFAULTS.lesson_image_model,
      artwork_image_model: data.artwork_image_model || DEFAULTS.artwork_image_model,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function CourseImageModelsEditor({ item }: { item: CourseSupportingDataItem }) {
  const [config, setConfig] = useState<ImageConfig>({ ...DEFAULTS });
  const [originalConfig, setOriginalConfig] = useState<ImageConfig>({ ...DEFAULTS });
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await courseSupportingDataService.get(item.asset_id);
        if (cancelled) return;
        const parsed = parseConfig(data.content);
        setConfig(parsed);
        setOriginalConfig(parsed);
        setEtag(data.etag);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item.asset_id]);

  const isDirty = config.lesson_image_model !== originalConfig.lesson_image_model ||
    config.artwork_image_model !== originalConfig.artwork_image_model;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const result = await courseSupportingDataService.update(
        item.asset_id,
        JSON.stringify(config, null, 2),
        etag,
      );
      setEtag(result.etag);
      setOriginalConfig(config);
      setSavedMessage('Saved successfully');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [config, etag, item.asset_id]);

  if (loading) {
    return <div className="p-6 text-gray-400 text-sm">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{item.name}</h2>
        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
      </div>

      <div className="bg-white border rounded p-4 space-y-4">
        {FIELDS.map(({ key, label, hint }) => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <select
              value={config[key]}
              onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))}
              className="w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {IMAGE_MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} — {o.provider}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">{hint}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save'}
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes affect all future image generation runs. Per-run overrides (e.g. the artwork panel) take precedence.</span>
      </div>
    </div>
  );
}
